import PropTypes from 'prop-types'
import React, { useEffect, useId, useRef, useState } from 'react'
import { feedbackData, isDisabled, reportProblems } from './Button'

// Controlled: the switch shows the requested state while onChange's request is
// pending, then the parent's checked prop again once it settles.
export function Switch({
  label,
  description,
  checked,
  onChange,
  onFailure,
  large = false,
  ...props
}) {
  const [requested, setRequested] = useState(null)
  const [state, setState] = useState({ phase: 'idle', pending: false })
  const action = useRef(null)
  const descriptionId = useId()
  const input = useRef(null)
  const failure = useRef(onFailure)
  failure.current = onFailure
  useEffect(() => {
    const { announce, createActionState, registerAction } = window.MEButtons
    const settle = () => setRequested(null)
    action.current = createActionState(
      state => {
        setState(state)
        if (state.cancelled) settle()
      },
      announce,
      error => {
        settle()
        failure.current?.(error)
      },
      settle
    )
    const unregister = registerAction(input.current, action.current)
    return () => {
      unregister()
      action.current.dispose()
    }
  }, [])
  const { stateLabel, validateAction } = window.MEButtons
  const data = feedbackData(props)
  const shown = requested ?? checked
  const blocked = state.pending || isDisabled(props)
  return (
    <label
      className={`me-btn-switch${large ? ' me-btn-switch-lg' : ''}`}
      data-me-react=""
    >
      <span>
        <span className="me-btn-label">
          {stateLabel(data, state.phase, label)}
        </span>
        {description && (
          <span className="me-btn-switch-desc" id={descriptionId}>
            {description}
          </span>
        )}
      </span>
      <input
        ref={input}
        {...props}
        type="checkbox"
        role="switch"
        aria-label={label}
        aria-describedby={
          description ? descriptionId : props['aria-describedby']
        }
        checked={shown}
        aria-checked={shown}
        aria-disabled={blocked || undefined}
        data-me-pending={state.pending ? '' : undefined}
        aria-busy={state.phase === 'busy' || undefined}
        className={
          state.phase === 'failure'
            ? 'is-failure'
            : state.phase === 'success'
            ? 'is-success'
            : undefined
        }
        onChange={event => {
          if (blocked) return
          const next = event.target.checked
          const problems = validateAction(data, false)
          reportProblems(problems, input.current)
          if (problems.length) return
          setRequested(next)
          let request
          try {
            request = onChange(next)
          } catch (error) {
            request = Promise.reject(error)
          }
          action.current.run(request, data)
        }}
      />
    </label>
  )
}

Switch.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.node,
  checked: PropTypes.bool.isRequired,
  // Receives the requested state and returns the request's promise.
  onChange: PropTypes.func.isRequired,
  onFailure: PropTypes.func,
  large: PropTypes.bool,
}
